import { useTranslation } from 'react-i18next';
import { useMyPermissions } from '../hooks/useMyPermissions';

/** Role code → chip colour class mapping */
function getRoleChipStyle(role: string): React.CSSProperties {
  if (role === 'ROLE_ADMIN') {
    return {
      color: 'var(--color-brand-primary)',
      borderColor: 'var(--color-brand-primary)',
      background: 'color-mix(in srgb, var(--color-brand-primary) 15%, transparent)',
    };
  }
  if (role === 'ROLE_MANAGER') {
    return {
      color: 'var(--color-warning)',
      borderColor: 'var(--color-warning)',
      background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
    };
  }
  if (role === 'ROLE_USER') {
    return {
      color: 'var(--color-info)',
      borderColor: 'var(--color-info)',
      background: 'color-mix(in srgb, var(--color-info) 12%, transparent)',
    };
  }
  // ROLE_GUEST or unknown
  return {
    color: 'var(--color-text-muted)',
    borderColor: 'var(--color-surface-border)',
    background: 'var(--color-surface-elevated)',
  };
}

const chipBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px var(--spacing-3)',
  borderRadius: 'var(--radius-full)',
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  border: '1px solid',
};

const permChipStyle: React.CSSProperties = {
  ...chipBase,
  color: 'var(--color-success)',
  borderColor: 'var(--color-success)',
  background: 'color-mix(in srgb, var(--color-success) 12%, transparent)',
};

function SkeletonLine({ width = '100%' }: { width?: string }) {
  return (
    <div
      aria-hidden="true"
      style={{
        height: 16,
        width,
        borderRadius: 'var(--radius-sm)',
        background: 'color-mix(in srgb, var(--color-text-muted) 20%, transparent)',
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

export function MyPermissionsTab() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useMyPermissions();

  if (isLoading) {
    return (
      <div
        aria-busy="true"
        aria-label={t('permissionDemo.myPerms.loading', '내 권한 로딩 중')}
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderLeft: '3px solid var(--color-brand-primary)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-3)',
        }}
      >
        <SkeletonLine width="40%" />
        <SkeletonLine width="70%" />
        <SkeletonLine width="55%" />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        role="alert"
        style={{
          padding: 'var(--spacing-8)',
          textAlign: 'center',
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-danger)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        <p style={{ color: 'var(--color-danger)', fontWeight: 600, margin: 0 }}>
          {t('permissionDemo.error.loadFailed', '데이터를 불러오지 못했습니다')}
        </p>
        <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)' }}>
          {error?.message ?? t('permissionDemo.myPerms.unknownError', '알 수 없는 오류')}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          style={{
            marginTop: 'var(--spacing-4)',
            background: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            padding: 'var(--spacing-2) var(--spacing-4)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {t('permissionDemo.error.retryButton', '다시 시도')}
        </button>
      </div>
    );
  }

  if (!data) return null;

  const formattedTime = (() => {
    try {
      return new Date(data.evaluatedAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
    } catch {
      return data.evaluatedAt;
    }
  })();

  return (
    <div>
      {/* Main permissions card */}
      <div
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderLeft: '3px solid var(--color-brand-primary)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        {/* User row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '110px 1fr',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-2) 0',
            borderBottom: '1px dashed var(--color-surface-border)',
            fontSize: 'var(--font-size-sm)',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              color: 'var(--color-text-secondary)',
              fontWeight: 600,
              fontSize: 'var(--font-size-xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t('permissionDemo.myPerms.user', '사용자')}
          </span>
          <span style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
            {data.userName}
          </span>
        </div>

        {/* Roles row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '110px 1fr',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-2) 0',
            borderBottom: '1px dashed var(--color-surface-border)',
            fontSize: 'var(--font-size-sm)',
            alignItems: 'flex-start',
          }}
        >
          <span
            style={{
              color: 'var(--color-text-secondary)',
              fontWeight: 600,
              fontSize: 'var(--font-size-xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              paddingTop: 4,
            }}
          >
            {t('permissionDemo.myPerms.rolesLabel', '역할 ({{count}})', { count: data.rolesCount })}
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
            {data.roles.length === 0 ? (
              <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic', fontSize: 'var(--font-size-sm)' }}>
                {t('permissionDemo.myPerms.empty', '없음')}
              </span>
            ) : (
              data.roles.map((role) => (
                <span
                  key={role}
                  style={{ ...chipBase, ...getRoleChipStyle(role) }}
                  aria-label={`${t('permissionDemo.myPerms.roleLabel', '역할')}: ${role}`}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 'var(--radius-full)',
                      background: 'currentColor',
                      flexShrink: 0,
                    }}
                  />
                  {role}
                </span>
              ))
            )}
          </div>
        </div>

        {/* Permissions row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '110px 1fr',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-2) 0',
            borderBottom: '1px dashed var(--color-surface-border)',
            fontSize: 'var(--font-size-sm)',
            alignItems: 'flex-start',
          }}
        >
          <span
            style={{
              color: 'var(--color-text-secondary)',
              fontWeight: 600,
              fontSize: 'var(--font-size-xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              paddingTop: 4,
            }}
          >
            {t('permissionDemo.myPerms.permissionsLabel', '권한 ({{count}})', { count: data.permissionsCount })}
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
            {data.permissions.length === 0 ? (
              <span
                style={{ color: 'var(--color-text-muted)', fontStyle: 'italic', fontSize: 'var(--font-size-sm)' }}
                aria-label={t('permissionDemo.myPerms.empty', '권한 없음')}
              >
                {t('permissionDemo.myPerms.empty', '권한 없음')}
              </span>
            ) : (
              data.permissions.map((perm) => (
                <span key={perm} style={permChipStyle} aria-label={`${t('permissionDemo.myPerms.permissionLabel', '권한')}: ${perm}`}>
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 'var(--radius-full)',
                      background: 'currentColor',
                      flexShrink: 0,
                    }}
                  />
                  {perm}
                </span>
              ))
            )}
          </div>
        </div>

        {/* Evaluated at row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '110px 1fr',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-2) 0',
            fontSize: 'var(--font-size-sm)',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              color: 'var(--color-text-secondary)',
              fontWeight: 600,
              fontSize: 'var(--font-size-xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t('permissionDemo.myPerms.evaluatedAt', '평가 시각')}
          </span>
          <span
            style={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
            }}
          >
            {formattedTime}
          </span>
        </div>
      </div>

      {/* Refresh button */}
      <button
        type="button"
        onClick={() => refetch()}
        style={{
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          color: 'var(--color-text-secondary)',
          padding: 'var(--spacing-2) var(--spacing-4)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'var(--font-family-sans)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
        }}
        aria-label={t('permissionDemo.myPerms.refreshButton', '권한 정보 새로 고침')}
      >
        ↻ {t('permissionDemo.myPerms.refreshButton', '새로 고침')}
      </button>
    </div>
  );
}
